import { Shield, Ban, Landmark, Info, type LucideIcon } from "lucide-react";
import { useWizard } from "@/contexts/WizardContext";
import { LockedBanner } from "../common/LockedBanner";
import { ProgressCard } from "../common/ProgressCard";
import { CardPanelHeader } from "../common/CardPanelHeader";
import { SectionCard } from "../common/SectionCard";
import { VoiceTextareaField } from "../common/VoiceTextareaField";
import type { StoryGovernanceData } from "@/contexts/wizard/types";

interface StoryGovernanceCardProps {
  onCollapse: () => void;
}

type StoryGovernanceFieldKey = keyof StoryGovernanceData;

type GovernanceSectionField = {
  key: StoryGovernanceFieldKey;
  id: string;
  label: string;
  placeholder: string;
  rows?: number;
};

type GovernanceSectionConfig = {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  fields: GovernanceSectionField[];
};

export function StoryGovernanceCard({ onCollapse }: StoryGovernanceCardProps) {
  const {
    storyGovernanceData: data,
    updateStoryGovernanceData: update,
    accountabilityData,
    forceSave,
    saveStatus,
    isLocked,
    isReadOnly,
    storyGovernanceCompletion,
  } = useWizard();

  const disabled = isLocked || isReadOnly;
  const hasSensitiveSectors = Object.values(accountabilityData.controversialSectorsRevenue || {}).some(
    (value) => value && value.amount && value.amount.trim().length > 0,
  );
  const hasExclusions = accountabilityData.benchmarkExclusion === true;

  const sections: GovernanceSectionConfig[] = [
    {
      id: "b11",
      title: "1. B11 Business Conduct",
      description: "Business conduct, sensitive sectors, governance diversity.",
      icon: Shield,
      fields: [
        {
          key: "b11BusinessConductApproach",
          id: "story-governance-b11-business-conduct-approach",
          label: "In 3-5 sentences, describe your approach to ethical business conduct (anti-corruption, conflicts of interest, gifts/hospitality, data/privacy if relevant).",
          placeholder: "e.g. We maintain a zero-tolerance approach to bribery and corruption. Our Code of Conduct covers conflicts of interest, gifts and hospitality limits, and data privacy compliance under GDPR...",
          rows: 4,
        },
        {
          key: "b11MisconductPrevention",
          id: "story-governance-b11-misconduct-prevention",
          label: "What mechanisms do you have to prevent misconduct? (e.g., code of conduct, approvals, training, audits, whistleblowing channel)",
          placeholder: "e.g. All employees sign our Code of Conduct annually. We run mandatory anti-corruption training, have a two-person approval process for transactions over €5,000, and operate an anonymous whistleblowing hotline...",
          rows: 4,
        },
        {
          key: "b11NotableUpdates",
          id: "story-governance-b11-notable-updates",
          label: "Any notable updates or actions this year to strengthen business conduct?",
          placeholder: "e.g. Launched a new online ethics training module, updated our gifts and hospitality policy, and completed an internal audit of procurement processes...",
          rows: 3,
        },
      ],
    },
    {
      id: "c9",
      title: "3. C9 Governance Diversity",
      description: "Describe your governance structure and decision-making (board/ownership/leadership, who oversees sustainability topics).",
      icon: Landmark,
      fields: [
        {
          key: "c9GovernanceStructure",
          id: "story-governance-c9-governance-structure",
          label: "Describe your governance structure and decision-making (board/ownership/leadership, who oversees sustainability topics).",
          placeholder: "e.g. We have a 5-member board of directors that meets quarterly. The CEO directly oversees sustainability strategy, supported by a Sustainability Committee that reports to the board bi-annually...",
          rows: 4,
        },
        {
          key: "c9DiversityInclusion",
          id: "story-governance-c9-diversity-inclusion",
          label: "How do you consider diversity and inclusion in governance/leadership (e.g., gender, skills, independence, representation)?",
          placeholder: "e.g. Our board includes 40% female representation. We prioritize diverse skills (finance, sustainability, legal) and have one independent director. Leadership recruitment actively seeks underrepresented candidates...",
          rows: 4,
        },
        {
          key: "c9ImprovementsGoals",
          id: "story-governance-c9-improvements-goals",
          label: "Any improvements or goals for governance diversity in the next 12-24 months?",
          placeholder: "e.g. Target 50% female board representation by 2027, add a second independent director with ESG expertise, introduce board skills matrix and annual effectiveness review...",
          rows: 3,
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <CardPanelHeader
        title={"Governance"}
        description={"Business conduct, sensitive sectors, governance diversity."}
        onClose={onCollapse}
        onSave={() => {
          void forceSave();
        }}
        saveStatus={saveStatus}
        disabled={disabled}
        tag={"Sustainability Story"}
      />

      {disabled && <LockedBanner />}

      <section className="form-surface-card-lg flex flex-col gap-5 bg-white p-5 sm:p-6">
        <div className="rounded-[24px] border border-primary/10 bg-primary/5 px-4 py-4 sm:px-5">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Info className="h-4 w-4" />
            </div>
            <p className="text-sm leading-6 text-slate-500">
              {"Fill in the"}{" "}
              <strong className="font-semibold text-slate-900">{"Governance"}</strong>{" "}
              {"tab first — the data you enter there (ethics, accountability, leadership) provides the foundation for the narrative fields below. Some fields here will only appear based on your governance answers."}
            </p>
          </div>
        </div>
      </section>

      {sections.slice(0, 1).map((section) => (
        <SectionCard
          key={section.id}
          header={
            <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[20px] bg-primary/10 text-primary">
                  <section.icon className="h-5 w-5" />
                </div>
                <div className="space-y-1.5">
                  <h3 className="text-xl font-semibold leading-7 text-slate-900">{section.title}</h3>
                  <p className="max-w-4xl text-sm leading-6 text-slate-500">{section.description}</p>
                </div>
              </div>
            </div>
          }
        >
          <div className="grid gap-4">
            {section.fields.map((field) => (
              <VoiceTextareaField
                key={field.key}
                id={field.id}
                label={field.label}
                placeholder={field.placeholder}
                value={data[field.key]}
                rows={field.rows}
                disabled={disabled}
                onChange={(value) => update({ [field.key]: value })}
              />
            ))}
          </div>
        </SectionCard>
      ))}

      <SectionCard
        header={
          <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[20px] bg-primary/10 text-primary">
                <Ban className="h-5 w-5" />
              </div>
              <div className="space-y-1.5">
                <h3 className="text-xl font-semibold leading-7 text-slate-900">{"2. C8 Sensitive Sectors / Exclusions"}</h3>
                <p className="max-w-4xl text-sm leading-6 text-slate-500">
                  {"Do you apply any exclusions or restrictions (what you do not do/do not support)?"}
                </p>
              </div>
            </div>
          </div>
        }
      >
        <div className="grid gap-4">
          {hasSensitiveSectors ? (
            <VoiceTextareaField
              id="story-governance-c8-sensitive-sectors"
              label={"Briefly describe which sensitive sectors/activities you operate in, finance, or derive revenue from, and why."}
              placeholder={"e.g. We derive approximately 15% of revenue from fossil fuel distribution services. This is due to our logistics contracts with energy companies while we transition our client base toward renewables..."}
              value={data.c8SensitiveSectors}
              rows={4}
              disabled={disabled}
              onChange={(value) => update({ c8SensitiveSectors: value })}
            />
          ) : null}

          {hasExclusions ? (
            <VoiceTextareaField
              id="story-governance-c8-exclusions"
              label={"Do you apply any exclusions or restrictions (what you do not do/do not support)?"}
              placeholder={"e.g. We exclude controversial weapons, tobacco manufacturing, and thermal coal mining from our investment portfolio and do not provide services to these sectors..."}
              value={data.c8Exclusions}
              rows={3}
              disabled={disabled}
              onChange={(value) => update({ c8Exclusions: value })}
            />
          ) : null}

          <VoiceTextareaField
            id="story-governance-c8-exclusions-practice"
            label={"How do you apply/check these exclusions in practice?"}
            placeholder={"e.g. All new clients are screened against our exclusion list during onboarding. Annual reviews of existing clients against updated sector criteria. Third-party data providers flag any changes..."}
            value={data.c8ExclusionsPractice}
            rows={3}
            disabled={disabled}
            onChange={(value) => update({ c8ExclusionsPractice: value })}
          />

          {!hasSensitiveSectors && !hasExclusions ? (
            <div className="rounded-[24px] border border-dashed border-slate-300 bg-white px-5 py-6">
              <p className="text-sm leading-6 text-slate-500">{"Additional fields will appear here if you report sensitive sector revenue or benchmark exclusions in the Governance tab."}</p>
            </div>
          ) : null}
        </div>
      </SectionCard>

      {sections.slice(1).map((section) => (
        <SectionCard
          key={section.id}
          header={
            <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[20px] bg-primary/10 text-primary">
                  <section.icon className="h-5 w-5" />
                </div>
                <div className="space-y-1.5">
                  <h3 className="text-xl font-semibold leading-7 text-slate-900">{section.title}</h3>
                  <p className="max-w-4xl text-sm leading-6 text-slate-500">{section.description}</p>
                </div>
              </div>
            </div>
          }
        >
          <div className="grid gap-4">
            {section.fields.map((field) => (
              <VoiceTextareaField
                key={field.key}
                id={field.id}
                label={field.label}
                placeholder={field.placeholder}
                value={data[field.key]}
                rows={field.rows}
                disabled={disabled}
                onChange={(value) => update({ [field.key]: value })}
              />
            ))}
          </div>
        </SectionCard>
      ))}

      <ProgressCard completion={storyGovernanceCompletion} />
    </div>
  );
}
